import React, { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

interface ProtectedRouteProps {
  isAuthenticated: boolean;
  openAuthModal: (mode: "signin" | "signup") => void;
  children: React.ReactElement;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  isAuthenticated,
  openAuthModal,
  children,
}) => {
  const [hasCheckedAuth, setHasCheckedAuth] = useState(false);
  const [shouldAllowAccess, setShouldAllowAccess] = useState(false);
  const location = useLocation();

  useEffect(() => {
    // Check if we have ANY indication the user should be logged in
    // This prevents race conditions where auth state hasn't loaded yet
    const checkAuthIndicators = () => {
      // Check localStorage for profile backups (indicates recent auth)
      const hasLocalProfile = localStorage.getItem('clubiq-user-profile');
      const hasPersistentBackup = Array.from({ length: localStorage.length }, (_, i) => 
        localStorage.key(i)
      ).some(key => key?.includes('PERSISTENT_PROFILE_') || key?.includes('quizBackup_'));
      
      // Check if there's an active Supabase session token in storage
      const hasSupabaseToken = localStorage.getItem('sb-aopqigmdlcqflkjeleua-auth-token') !== null;
      
      const hasAuthIndicator = hasLocalProfile || hasPersistentBackup || hasSupabaseToken;
      
      console.log('🔒 ProtectedRoute auth check:', {
        isAuthenticated,
        hasLocalProfile: !!hasLocalProfile,
        hasPersistentBackup,
        hasSupabaseToken,
        path: location.pathname
      });
      return hasAuthIndicator;
    };

    // Give auth state MORE time to settle
    // Use multiple checks with increasing delays to catch late-loading auth
    let checkCount = 0;
    const maxChecks = 5;
    
    const checkAuth = () => {
      checkCount++;
      
      if (isAuthenticated) {
        // User is authenticated - allow access immediately
        console.log('✅ ProtectedRoute: User authenticated');
        setShouldAllowAccess(true);
        setHasCheckedAuth(true);
        return;
      }
      
      // Check for auth indicators that suggest we should wait
      const hasAuthIndicator = checkAuthIndicators();
      
      if (hasAuthIndicator && checkCount < maxChecks) {
        // Auth indicators present but not authenticated yet - wait longer
        console.log(`⏳ ProtectedRoute: Auth indicators present, waiting... (check ${checkCount}/${maxChecks})`);
        setTimeout(checkAuth, 500); // Check again in 500ms
        return;
      }
      
      // No auth after all checks - redirect
      console.log('🚫 ProtectedRoute: No auth after checks, redirecting');
      setHasCheckedAuth(true);
      setShouldAllowAccess(false);
      
      // Only open modal if we're not already on an auth-related flow
      if (!location.pathname.includes('reset-password')) {
        openAuthModal("signin");
      }
    };

    // Start checking after initial delay
    const timer = setTimeout(checkAuth, 300);

    return () => clearTimeout(timer);
  }, [isAuthenticated, openAuthModal, location.pathname]);

  // Also update access if isAuthenticated changes after initial check
  useEffect(() => {
    if (isAuthenticated && hasCheckedAuth) {
      setShouldAllowAccess(true);
    }
  }, [isAuthenticated, hasCheckedAuth]);

  // Show loading while checking auth
  if (!hasCheckedAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!shouldAllowAccess && !isAuthenticated) {
    return <Navigate to="/" replace />;
  }

  return children;
};

export default ProtectedRoute;


